import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CustomHttpUrlEncodingCodec } from '../encoder';
import { WorkShiftSessionInputDto } from '../model/workShiftSessionInputDto';
import { WorkShiftSessionOutputDto } from '../model/workShiftSessionOutputDto';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

export interface PaginatedWorkShiftSessionsResponse {
  data: WorkShiftSessionOutputDto[];
  pageNumber?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

@Injectable()
export class WorkShiftSessionService {
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();
  protected basePath = '/';

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  public getAll(
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<WorkShiftSessionOutputDto[]> {
    let headers = this.defaultHeaders;
    if (
      this.configuration.apiKeys &&
      this.configuration.apiKeys.Authorization
    ) {
      headers = headers.set(
        'Authorization',
        this.configuration.apiKeys.Authorization
      );
    }
    headers = headers.set('Accept', 'application/json');
    return this.httpClient.request<WorkShiftSessionOutputDto[]>(
      'get',
      `${this.basePath}/api/WorkShiftSession/GetAll`,
      {
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public getAllPaged(
    pageNumber: number = 1,
    pageSize: number = 30,
    cdDelivery?: number,
    from?: string,
    to?: string,
    clientName?: string,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<PaginatedWorkShiftSessionsResponse> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (pageNumber !== undefined && pageNumber !== null) {
      queryParameters = queryParameters.set(
        'pageNumber',
        String(pageNumber)
      );
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParameters = queryParameters.set('pageSize', String(pageSize));
    }
    if (cdDelivery !== undefined && cdDelivery !== null) {
      queryParameters = queryParameters.set(
        'cdDelivery',
        String(cdDelivery)
      );
    }
    if (from !== undefined && from !== null) {
      queryParameters = queryParameters.set('from', from);
    }
    if (to !== undefined && to !== null) {
      queryParameters = queryParameters.set('to', to);
    }
    if (clientName !== undefined && clientName !== null) {
      queryParameters = queryParameters.set('clientName', clientName);
    }

    let headers = this.defaultHeaders;
    if (
      this.configuration.apiKeys &&
      this.configuration.apiKeys.Authorization
    ) {
      headers = headers.set(
        'Authorization',
        this.configuration.apiKeys.Authorization
      );
    }
    headers = headers.set('Accept', 'application/json');
    return this.httpClient.request<PaginatedWorkShiftSessionsResponse>(
      'get',
      `${this.basePath}/api/WorkShiftSession/GetAll`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public getById(
    id: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<WorkShiftSessionOutputDto> {
    let headers = this.defaultHeaders;
    if (
      this.configuration.apiKeys &&
      this.configuration.apiKeys.Authorization
    ) {
      headers = headers.set(
        'Authorization',
        this.configuration.apiKeys.Authorization
      );
    }
    headers = headers.set('Accept', 'application/json');
    return this.httpClient.request<WorkShiftSessionOutputDto>(
      'get',
      `${this.basePath}/api/WorkShiftSession/GetById/${encodeURIComponent(
        String(id)
      )}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public create(
    cdDelivery: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<number> {
    const body: WorkShiftSessionInputDto = {
      cdDelivery,
      startTime: new Date().toISOString(),
    };
    return this.createWithBody(body, observe, reportProgress);
  }

  public createWithBody(
    body: WorkShiftSessionInputDto,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<number> {
    let headers = this.defaultHeaders;
    if (
      this.configuration.apiKeys &&
      this.configuration.apiKeys.Authorization
    ) {
      headers = headers.set(
        'Authorization',
        this.configuration.apiKeys.Authorization
      );
    }
    headers = headers
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');
    return this.httpClient.request<number>(
      'post',
      `${this.basePath}/api/WorkShiftSession/Create`,
      {
        body,
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }

  public endShift(
    shiftSessionId: number,
    observe: 'body' = 'body',
    reportProgress: boolean = false
  ): Observable<boolean> {
    let headers = this.defaultHeaders;
    if (
      this.configuration.apiKeys &&
      this.configuration.apiKeys.Authorization
    ) {
      headers = headers.set(
        'Authorization',
        this.configuration.apiKeys.Authorization
      );
    }
    headers = headers.set('Accept', 'application/json');
    return this.httpClient.request<boolean>(
      'post',
      `${this.basePath}/api/WorkShiftSession/EndShift/${encodeURIComponent(
        String(shiftSessionId)
      )}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }
}
